import {
	MailingAddressInput,
	CustomerAddressCreatePayload,
	CustomerDefaultAddressUpdatePayload,
	CustomerAddressUpdatePayload,
	MailingAddress,
} from '@local/types/storefront-api-types'
import {postWithLocale, fetchWithLocale, patchWithLocale, deleteWithLocale} from '../useShopifyStore'
import {addressIdParsed} from '@local/utils/shopify'

export interface ShopifyCustomerAddressProps {
	defaultAddressId?: string,
	defaultAddress?: MailingAddress,
	addresses?: MailingAddress[],
	getAddresses: () => Promise<MailingAddress[] | null>,
	getDefaultAddressId: () => Promise<string | null | undefined>,
	setDefaultAddress: (id: string) => Promise<CustomerDefaultAddressUpdatePayload>,
	addAddress: (input: MailingAddressInput, makeDefault: boolean) => Promise<CustomerAddressCreatePayload>,
	updateAddress: (id: string, input: MailingAddressInput, makeDefault: boolean) => Promise<CustomerAddressUpdatePayload>,
	deleteAddress: (id: string) => Promise<void | null>,
}

export const shopifyCustomerAddressStore = (set, get, store): ShopifyCustomerAddressProps => ({
	getAddresses: async () => {
		const customerAccessToken = get().customerAccessToken?.accessToken
		if (!customerAccessToken) return null
		set({loading: true})
		const result = await fetchWithLocale('/api/shopify/customer/addresses', {searchParams: {customerAccessToken}})
		const addresses = result as MailingAddress[]

		set({
			addresses,
			loading: false,
		})

		await get().getDefaultAddressId()

		return addresses
	},

	getDefaultAddressId: async () => {
		const customerAccessToken = get().customerAccessToken?.accessToken
		if (!customerAccessToken) return null
		set({loading: true})
		const result = await fetchWithLocale('/api/shopify/customer/defaultAddress', {searchParams: {customerAccessToken}})
		const defaultAddressId = result?.customer?.defaultAddress?.id
		const defaultAddress = get().addresses?.find(address => address.id === defaultAddressId)
		set({
			defaultAddress,
			defaultAddressId,
			loading: false,
		})
		return defaultAddressId
	},

	setDefaultAddress: async (addressId: string) => {
		const customerAccessToken = get().customerAccessToken?.accessToken
		if (!customerAccessToken) return null
		set({loading: true})
		const result = await postWithLocale('/api/shopify/customer/defaultAddress', {customerAccessToken, addressId})
		const defaultAddressId = result?.customerDefaultAddressUpdate?.customer?.defaultAddress?.id
		const defaultAddress = get().addresses?.find(address => address.id === defaultAddressId)
		set({
			defaultAddress,
			defaultAddressId,
			loading: false,
		})
		return result?.customerDefaultAddressUpdate
	},

	addAddress: async (address, makeDefault) => {
		const customerAccessToken = get().customerAccessToken?.accessToken
		if (!customerAccessToken) return null
		set({loading: true})
		const result = await postWithLocale('/api/shopify/customer/addresses', {customerAccessToken, address})
		const createdAddress = result?.customerAddressCreate?.customerAddress
		if (createdAddress) {
			if (makeDefault) {
				await get().setDefaultAddress(createdAddress.id)
			}
			set({
				addresses: [...get().addresses, createdAddress],
				loading: false,
			})
		} else {
			set({loading: false})
		}
		return result?.customerAddressCreate
	},

	updateAddress: async (id, address, makeDefault) => {
		const customerAccessToken = get().customerAccessToken?.accessToken
		if (!customerAccessToken) return null
		set({loading: true})
		const result = await patchWithLocale('/api/shopify/customer/addresses', {customerAccessToken, id, address})
		const updatedAddress = result?.customerAddressUpdate?.customerAddress
		if (updatedAddress && makeDefault) {
			if (makeDefault) {
				await get().setDefaultAddress(updatedAddress.id)
			}
		}
		set({
			addresses: get().addresses.map(address => addressIdParsed(address.id) === addressIdParsed(updatedAddress.id) ? updatedAddress : address),
			loading: false,
		})

		return result?.customerAddressUpdate
	},

	deleteAddress: async (id) => {
		const customerAccessToken = get().customerAccessToken?.accessToken
		if (!customerAccessToken) return null
		set({loading: true})
		const result = await deleteWithLocale('/api/shopify/customer/addresses', undefined, {searchParams: {id, customerAccessToken}})
		const deletedAddressId = result?.customerAddressDelete?.deletedCustomerAddressId
		let newDefault
		if (deletedAddressId) {
			if (deletedAddressId === get().defaultAddressId) {
				// default address was deleted. which address is the new default?
				newDefault = await get().getDefaultAddressId()
			}
			set(current => ({
				defaultAddressId: deletedAddressId === current.defaultAddressId ? newDefault : undefined,
				addresses: current.addresses.filter(address => addressIdParsed(address.id) !== addressIdParsed(deletedAddressId)),
				loading: false,
			}))
		}
	},
})
